<script lang="ts" module>
  export interface StatStripItem {
    label: string;
    value: string;
    sub?: string;
    /** Colours the value. */
    tone?: 'default' | 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'muted';
    /** Small colour dot before the label (client, series). */
    color?: string;
    delta?: { current: number; previous: number | null; invert?: boolean };
  }
</script>

<script lang="ts">
  // A row of small totals (label + value), lighter than a StatGrid of cards.
  //   divided  one surface, columns split by hairlines (totals above a table)
  //   tiles    separate soft tiles (inside a card or a dialog)
  //   inline   one wrapping line "Label value · Label value" (summaries, bars)
  //   <StatStrip items={[{ label: 'Uren', value: '32:15' }, { label: 'Openstaand', value: '€ 420', tone: 'warning' }]} />
  // Columns collapse to two on narrow containers.
  import ColorDot from './ColorDot.svelte';
  import Delta from './Delta.svelte';

  interface Props {
    items: StatStripItem[];
    variant?: 'divided' | 'tiles' | 'inline';
    size?: 'sm' | 'md';
    /** divided only: draw the card surface (default) or just the dividers. */
    card?: boolean;
    ariaLabel?: string;
    class?: string;
  }
  let { items, variant = 'divided', size = 'md', card = true, ariaLabel, class: cls = '' }: Props = $props();
</script>

<div class="strip-wrap {cls}">
  <dl class="strip {variant} {size}" class:card={variant === 'divided' && card} aria-label={ariaLabel} style:--n={items.length} style:--n2={Math.min(items.length, 2)}>
    {#each items as item, i (item.label + i)}
      <div class="item">
        <dt class="label">
          {#if item.color}<ColorDot color={item.color} size="xs" />{/if}
          <span class="truncate">{item.label}</span>
        </dt>
        <dd class="value tabular tone-{item.tone || 'default'}">
          <span class="truncate">{item.value}</span>
          {#if item.delta}<Delta current={item.delta.current} previous={item.delta.previous} invert={item.delta.invert} size="sm" />{/if}
        </dd>
        {#if item.sub}<dd class="sub">{item.sub}</dd>{/if}
      </div>
    {/each}
  </dl>
</div>

<style>
  .strip-wrap { container-type: inline-size; min-width: 0; }
  .strip { margin: 0; min-width: 0; }
  dd { margin: 0; }
  .item { min-width: 0; display: flex; flex-direction: column; gap: var(--space-0_5); }
  .label {
    display: flex;
    align-items: center;
    gap: var(--space-1_5);
    font-size: var(--text-xs);
    font-weight: var(--weight-medium);
    color: var(--text-2);
    min-width: 0;
  }
  .value {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-lg);
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-snug);
    color: var(--text);
    min-width: 0;
    line-height: var(--leading-tight);
  }
  .sm .value { font-size: var(--text-md); }
  .sub { font-size: var(--text-xs); color: var(--text-3); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .tone-brand { color: var(--brand); }
  .tone-success { color: var(--success); }
  .tone-warning { color: var(--warning); }
  .tone-danger { color: var(--danger); }
  .tone-info { color: var(--info); }
  .tone-muted { color: var(--text-2); }

  /* divided */
  .divided { display: grid; grid-template-columns: repeat(var(--n), minmax(0, 1fr)); }
  .divided .item { padding: var(--space-3) var(--space-4); }
  .divided.sm .item { padding: var(--space-2) var(--space-3); }
  .divided .item + .item { border-left: 1px solid var(--border); }
  .divided.card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); box-shadow: var(--shadow-xs); }
  .divided:not(.card) .item:first-child { padding-left: 0; }

  /* tiles */
  .tiles { display: grid; grid-template-columns: repeat(var(--n), minmax(0, 1fr)); gap: var(--space-2); }
  .tiles .item { padding: var(--space-2_5) var(--space-3); background: var(--surface-2); border: 1px solid var(--border); border-radius: var(--radius-sm); }
  .tiles.sm .item { padding: var(--space-2) var(--space-2_5); }

  /* inline */
  .inline { display: flex; flex-wrap: wrap; align-items: baseline; column-gap: var(--space-4); row-gap: var(--space-1); }
  .inline .item { flex-direction: row; align-items: baseline; gap: var(--space-1_5); }
  .inline .value { font-size: var(--text-base); }
  .inline.sm .value { font-size: var(--text-sm); }
  .inline .sub { align-self: baseline; }

  @container (max-width: 520px) {
    .divided, .tiles { grid-template-columns: repeat(var(--n2), minmax(0, 1fr)); }
    .divided .item + .item { border-left: 0; }
    .divided .item:nth-child(even) { border-left: 1px solid var(--border); }
    .divided .item:nth-child(n + 3) { border-top: 1px solid var(--border); }
  }
</style>
